import gsap from "gsap";
import { MotionPathPlugin } from "gsap/MotionPathPlugin";
import type { EasingType } from "../types/timeline";
import type { ClipMotionPathPoint } from "./clip-motion-path";

gsap.registerPlugin(MotionPathPlugin);

/** Alias kept for the on canvas overlay; the shape is defined with the path. */
export type GSAPMotionPathPoint = ClipMotionPathPoint;

/** View model the on canvas overlay draws from. Persisted paths use
 * `ClipMotionPath` in clip-motion-path.ts. */
export interface MotionPathConfig {
  clipId: string;
  enabled: boolean;
  pathType: "linear" | "bezier" | "catmull-rom";
  points: GSAPMotionPathPoint[];
  showPath: boolean;
  autoOrient: boolean;
  alignOrigin: [number, number];
}

export interface GSAPAnimationConfig {
  duration: number;
  ease: string;
  delay?: number;
  repeat?: number;
  yoyo?: boolean;
}

const GSAP_EASING_MAP: Record<EasingType, string> = {
  linear: "none",
  ease: "power2.inOut",
  "ease-in": "power2.in",
  "ease-out": "power2.out",
  "ease-in-out": "power2.inOut",
  hold: "steps(1)",
  bezier: "power2.inOut",
  smoothstep: "power2.inOut",
  smootherstep: "power3.inOut",
  snappy: "expo.out",
  smooth: "power2.inOut",
  easeInQuad: "power1.in",
  easeOutQuad: "power1.out",
  easeInOutQuad: "power1.inOut",
  easeInCubic: "power2.in",
  easeOutCubic: "power2.out",
  easeInOutCubic: "power2.inOut",
  easeInQuart: "power3.in",
  easeOutQuart: "power3.out",
  easeInOutQuart: "power3.inOut",
  easeInQuint: "power4.in",
  easeOutQuint: "power4.out",
  easeInOutQuint: "power4.inOut",
  easeInSine: "sine.in",
  easeOutSine: "sine.out",
  easeInOutSine: "sine.inOut",
  easeInExpo: "expo.in",
  easeOutExpo: "expo.out",
  easeInOutExpo: "expo.inOut",
  easeInCirc: "circ.in",
  easeOutCirc: "circ.out",
  easeInOutCirc: "circ.inOut",
  easeInBack: "back.in",
  easeOutBack: "back.out",
  easeInOutBack: "back.inOut",
  easeInBackStrong: "back.in(2.7)",
  easeOutBackStrong: "back.out(2.7)",
  easeInOutBackStrong: "back.inOut(2.7)",
  easeInElastic: "elastic.in",
  easeOutElastic: "elastic.out",
  easeInOutElastic: "elastic.inOut",
  easeInElasticSoft: "elastic.in(0.65,0.3)",
  easeOutElasticSoft: "elastic.out(0.65,0.3)",
  easeInOutElasticSoft: "elastic.inOut(0.65,0.3)",
  easeInBounce: "bounce.in",
  easeOutBounce: "bounce.out",
  easeInOutBounce: "bounce.inOut",
  easeInBounceSmall: "bounce.in",
  easeOutBounceSmall: "bounce.out",
  easeInOutBounceSmall: "bounce.inOut",
};

export function easingToGSAP(easing: EasingType): string {
  return GSAP_EASING_MAP[easing] || "none";
}

class GSAPAnimationEngine {
  private timelines: Map<string, gsap.core.Timeline> = new Map();

  createTimeline(clipId: string, config?: GSAPAnimationConfig): gsap.core.Timeline {
    if (this.timelines.has(clipId)) {
      this.timelines.get(clipId)!.kill();
    }

    const timeline = gsap.timeline({
      paused: true,
      defaults: {
        duration: config?.duration || 1,
        ease: config?.ease || "none",
      },
    });

    this.timelines.set(clipId, timeline);
    return timeline;
  }

  getTimeline(clipId: string): gsap.core.Timeline | undefined {
    return this.timelines.get(clipId);
  }

  removeTimeline(clipId: string): void {
    const timeline = this.timelines.get(clipId);
    if (timeline) {
      timeline.kill();
      this.timelines.delete(clipId);
    }
  }

  dispose(): void {
    for (const timeline of this.timelines.values()) {
      timeline.kill();
    }
    this.timelines.clear();
  }
}

let gsapEngineInstance: GSAPAnimationEngine | null = null;

export function getGSAPEngine(): GSAPAnimationEngine {
  if (!gsapEngineInstance) {
    gsapEngineInstance = new GSAPAnimationEngine();
  }
  return gsapEngineInstance;
}

export function disposeGSAPEngine(): void {
  if (gsapEngineInstance) {
    gsapEngineInstance.dispose();
    gsapEngineInstance = null;
  }
}

export { GSAPAnimationEngine };
